// src/pages/users/useUsersPage.ts
import { useEffect, useMemo, useState } from "react";

import { useUsers, useUserModules } from "@/api/features/users/queries";
import {
  useCreateUserMutation,
  useUpdateUserMutation,
  useDeleteUserMutation,
  useGrantUserModulesMutation,
  useRevokeUserModulesMutation,
  useResetUserPasswordMutation,
} from "@/api/features/users/mutations";

import { useModules } from "@/api/features/modules";
import { useRoles } from "@/api/features/roles/queries";

import type {
  UserResult,
  PaginatedUsers,
  CreateUserBody,
  UpdateUserBody,
} from "@/api/features/users/types";
import type { Role as RoleModel } from "@/api/features/roles/types";

import type { UserTableFilters } from "@/components/users/UserTable";
import type { UpsertUser } from "@/components/users/forms/CreateUserForm";
import type { User as UiUser } from "@/components/users/forms/EditUserForm";

import {
  DEFAULT_PAGE_SIZE,
  MAX_PAGE_SIZE,
} from "@/constants/pagination";

/* ---------------------------------------------------------- */
/* Helpers                                                     */
/* ---------------------------------------------------------- */

export function mapUpsertToCreate(payload: UpsertUser): CreateUserBody {
  // Assuming UpsertUser already matches CreateUserBody shape
  // (including password, roleId, optional profile/address fields).
  // If UpsertUser allows roleId: number | null, you might want to
  // assert/validate here before sending to API.
  return {
    ...payload,
  } as CreateUserBody;
}

export function mapUiToUpdate(payload: UiUser): UpdateUserBody {
  // Strip out UI-only fields, keep only API fields.
  const { id, roleName, ...rest } = payload;
  return {
    ...rest,
  } as UpdateUserBody;
}

export function extractErrorMessage(err: any): string {
  const data =
    (err && err.payload) ||
    err?.response?.data ||
    err;

  const fieldErrors = data?.errors?.fieldErrors;
  if (fieldErrors) {
    for (const msgs of Object.values(fieldErrors)) {
      if (Array.isArray(msgs) && msgs.length > 0) return msgs[0];
    }
  }

  const formErrors = data?.errors?.formErrors;
  if (formErrors?.length) return formErrors[0];

  if (data?.message) return data.message;

  return "Something went wrong. Please try again.";
}

/* ---------------------------------------------------------- */
/* Main Hook                                                   */
/* ---------------------------------------------------------- */

export function useUsersPage() {
  /* ---------------- Filters, search, page ---------------- */
  const [filters, setFilters] = useState<UserTableFilters>({});
  const [globalQ, setGlobalQ] = useState("");
  const [page, setPage] = useState(1);
  const pageSize = DEFAULT_PAGE_SIZE;

  // Reset to first page whenever filters or global search change
  useEffect(() => setPage(1), [filters, globalQ]);

  /* ---------------- Users (API) ---------------- */
  const usersQuery = useUsers({
    page: 1,                // backend page (we fetch all and paginate client-side)
    limit: MAX_PAGE_SIZE,
    q: globalQ || undefined,
  });

  const apiUsers: UserResult[] =
    (usersQuery.data as PaginatedUsers | undefined)?.items ?? [];

  /* ---------------- Roles (for roleName mapping) ---------------- */
  const rolesQuery = useRoles({
    page: 1,
    pageSize: MAX_PAGE_SIZE,
  });

  const roles: RoleModel[] = rolesQuery.data?.items ?? [];

  const roleById = useMemo(() => {
    const m = new Map<number, string>();
    roles.forEach((r) => m.set(r.id, r.name));
    return m;
  }, [roles]);

  /* -------- Map API user → UI user for tables/forms -------- */
  const allRows: UiUser[] = useMemo(
    () =>
      apiUsers.map((u) => ({
        id: u.id,
        firstName: u.firstName,
        lastName: u.lastName,
        username: u.username,
        email: u.email,
        phone: u.phone ?? "",

        roleId: u.roleId ?? null,
        roleName: u.roleId ? roleById.get(u.roleId) ?? null : null,

        // Profile
        gender: u.gender ?? undefined,
        dob: u.dob ?? undefined,
        idType: u.idType ?? undefined,
        idNumber: u.idNumber ?? undefined,

        // Address
        addressLine1: u.addressLine1 ?? undefined,
        addressLine2: u.addressLine2 ?? undefined,
        country: u.country ?? undefined,
        state: u.state ?? undefined,
        city: u.city ?? undefined,
      })),
    [apiUsers, roleById]
  );

  /* ---------------- Client-side filtering ---------------- */
  const filtered = useMemo(() => {
    const f = filters;

    return allRows.filter((u) => {
      const matchFirst =
        !f.firstName ||
        u.firstName.toLowerCase().includes(f.firstName.toLowerCase());

      const matchLast =
        !f.lastName ||
        u.lastName.toLowerCase().includes(f.lastName.toLowerCase());

      const matchEmail =
        !f.email ||
        u.email.toLowerCase().includes(f.email.toLowerCase());

      const matchPhone =
        !f.phone ||
        u.phone.toLowerCase().includes(f.phone.toLowerCase());

      const matchRole =
        !f.role || u.roleName === f.role;

      return (
        matchFirst &&
        matchLast &&
        matchEmail &&
        matchPhone &&
        matchRole
      );
    });
  }, [allRows, filters]);

  const totalFiltered = filtered.length;
  const totalPages = Math.max(1, Math.ceil(totalFiltered / pageSize));
  const start = (page - 1) * pageSize;
  const pageRows = filtered.slice(start, start + pageSize);
  const serialStart = start;

  /* ---------------- Error state ---------------- */
  const [createError, setCreateError] = useState<string | null>(null);
  const [updateError, setUpdateError] = useState<string | null>(null);

  /* ---------------- CRUD mutations ---------------- */
  const createUserMutation = useCreateUserMutation();
  const updateUserMutation = useUpdateUserMutation(); // see note in header
  const deleteUserMutation = useDeleteUserMutation();

  const onCreate = (payload: UpsertUser, opts?: { onSuccess?: () => void }) => {
    const dto = mapUpsertToCreate(payload);
    setCreateError(null);

    createUserMutation.mutate(dto, {
      onSuccess: () => opts?.onSuccess?.(),
      onError: (err) => setCreateError(extractErrorMessage(err)),
    });
  };

  const onEdit = (payload: UiUser, opts?: { onSuccess?: () => void }) => {
    if (!payload.id) return;
    const dto = mapUiToUpdate(payload);
    setUpdateError(null);

    updateUserMutation.mutate(
      { id: payload.id, data: dto },
      {
        onSuccess: () => opts?.onSuccess?.(),
        onError: (err) => setUpdateError(extractErrorMessage(err)),
      }
    );
  };

  const onDelete = (id: number, opts?: { onSuccess?: () => void }) => {
    deleteUserMutation.mutate(id, {
      onSuccess: () => opts?.onSuccess?.(),
    });
  };

  /* ---------------- Modules assignment ---------------- */
  const [manageModulesUser, setManageModulesUser] = useState<UiUser | null>(null);

  const modulesQuery = useModules({
    page: 1,
    limit: MAX_PAGE_SIZE,
  });

  const userModulesQuery = useUserModules(manageModulesUser?.id ?? null);

  const grantModulesMutation = useGrantUserModulesMutation();
  const revokeModulesMutation = useRevokeUserModulesMutation();

  const userModuleIds = new Set(
    (userModulesQuery.data ?? []).map((m) => m.id)
  );

  const modulesBusy =
    grantModulesMutation.isPending ||
    revokeModulesMutation.isPending;

  const handleToggleModule = (moduleId: number, nextChecked: boolean) => {
    if (!manageModulesUser) return;

    if (nextChecked) {
      grantModulesMutation.mutate({
        userId: manageModulesUser.id,
        moduleIds: [moduleId],
      });
    } else {
      revokeModulesMutation.mutate({
        userId: manageModulesUser.id,
        moduleIds: [moduleId],
      });
    }
  };

  /* ---------------- Reset password ---------------- */
  const resetPasswordMutation = useResetUserPasswordMutation();

  const onResetPassword = (id: number | string) => {
    const numericId = Number(id);
    const target = allRows.find((u) => u.id === numericId);
    if (!target?.email) return;
    resetPasswordMutation.mutate(target.email);
  };

  /* ---------------- Return API ---------------- */
  return {
    // Data
    allRows,
    pageRows,
    serialStart,
    totalFiltered,
    totalPages,
    page,
    pageSize,
    start,

    // Filters & search
    filters,
    setFilters,
    globalQ,
    setGlobalQ,
    setPage,

    // Loading
    usersLoading: usersQuery.isLoading || usersQuery.isFetching,

    // CRUD actions
    onCreate,
    onEdit,
    onDelete,
    onResetPassword,

    // Mutations
    createUserMutation,
    updateUserMutation,
    deleteUserMutation,

    // Error messages
    createError,
    updateError,

    // Modules assignment
    manageModulesUser,
    setManageModulesUser,
    modulesQuery,
    userModulesQuery,
    userModuleIds,
    modulesBusy,
    handleToggleModule,

    // Reset password
    resetPasswordMutation,
  };
}
